// EXEMPT
import React, {
  Fragment,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { css, styled } from "goober";
import { touchOnly } from "clutch/src/Style/style.mjs";

import { readState } from "@/__main__/app-state.mjs";
import { IS_APP } from "@/__main__/constants.mjs";
import MenuCategory from "@/app/AppShellMenuCategory.jsx";
import NavProfile from "@/app/AppShellNavProfile.jsx";
import {
  GAME_ACTIVE_MAP,
  GAME_BOX_ICONS,
  GAME_COLORS,
  GAME_NAME_MAP,
  GAME_SHORT_NAMES,
} from "@/app/constants.mjs";
import gamesList from "@/app/games.mjs";
import { getNavProfile, useCurrentAccount } from "@/app/util.mjs";
import GuestProfile from "@/inline-assets/nav-guest-profile.svg";
import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogWindow,
} from "@/shared/Dialog.jsx";
import FullscreenOverlay from "@/shared/FullscreenOverlay.jsx";
import { classNames } from "@/util/class-names.mjs";
import { useCurrentGame } from "@/util/game-route.mjs";
import gameShortNameReassign from "@/util/game-short-name-reassign.mjs";
import isEmpty from "@/util/is-empty.mjs";
import isObject from "@/util/is-object.mjs";
import { useRoute } from "@/util/router-hooks.mjs";
import useDropdownRef from "@/util/use-dropdown-ref.mjs";
import { sendInteractionEvent } from "@/util/use-interaction-event.mjs";
import useMediaQuery from "@/util/use-media-query.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const GamesProfilesWrapper = styled("div", React.forwardRef)`
  position: relative;
  display: inline-flex;
  align-items: center;

  .nav-profile-link {
    display: flex;
    align-items: center;
    height: var(--secondary-nav-height);
    color: var(--shade0-50);

    &:hover {
      color: var(--shade0-75);
    }
    &.active {
      color: var(--shade0);
    }
  }

  .profile-dropdown {
    position: absolute;
    max-height: 80vh;
    overflow-y: auto;
    top: calc(100% - var(--sp-1));
    left: 0px;
    padding: var(--sp-1);
    background: var(--shade7);
    border-radius: var(--br-lg);
    box-shadow:
      inset 0 0 0 1px var(--shade3-15),
      0px 12px 24px 0px rgba(7, 8, 12, 0.25),
      0px 4px 12px 0px rgba(7, 8, 12, 0.25);

    .category {
      display: flex;
      padding: var(--sp-3) var(--sp-2) var(--sp-1) var(--sp-2);
      align-items: center;
      gap: var(--sp-2);
      align-self: stretch;

      > span {
        color: var(--shade2);
      }
    }
  }
`;

const ProfileLink = () => css`
  --link-bg-color: var(--shade7);
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  height: var(--sp-11);
  padding: var(--sp-2) var(--sp-3);
  background: var(--link-bg-color);
  border: 2px solid transparent;
  box-shadow: inset 0 0 0 2px var(--shade7);
  border-radius: var(--br-lg);
  transition:
    border-color var(--transition),
    background var(--transition);

  &:hover {
    --link-bg-color: var(--shade6);
    border-color: var(--shade4);
  }

  .profile-avatar {
    position: relative;

    .avatar {
      width: var(--sp-7);
      height: var(--sp-7);
      background-color: var(--shade4);
      background-image: var(--bg);
      background-size: cover;
      background-position: center;
      border-radius: 50%;
    }

    svg {
      position: absolute;
      bottom: 0;
      right: -0.25rem;
      background: var(--game-color, var(--shade4));
      border-radius: var(--br-sm);
      width: var(--sp-3);
      height: var(--sp-3);
      box-shadow: 0 0 0 3px var(--link-bg-color);
    }

    path {
      fill: var(--shade9);
    }
  }

  .title {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
  }

  .name {
    white-space: nowrap;
  }

  .shard {
    color: var(--shade1);
    padding: var(--sp-0_5) var(--sp-1);
    background: var(--shade1-15);
    border-radius: var(--br-sm);
  }
`;

const ProfileMenuLink = ({
  profile: { imgUrl, profileUrl, name, tagLine, shard, gameSymbol },
  onClick,
}) => {
  const GameIcon = GAME_BOX_ICONS[gameSymbol];
  const GameColor = GAME_COLORS[gameSymbol];
  const ref = useRef(null);

  // Due to some complex interactions between native events and
  // React's SyntheticEvent, sometimes the SyntheticEvent doesn't
  // propagate. Using native events avoids this issue.
  useLayoutEffect(() => {
    const { current: el } = ref;
    if (!el || !onClick) return;
    const handleClick = (e) => {
      onClick(e);
      sendInteractionEvent("app-navigation-profile-menu-link");
    };
    el.addEventListener("click", handleClick);
    return () => {
      el.removeEventListener("click", handleClick);
    };
  }, [onClick]);

  return (
    <a
      href={profileUrl}
      ref={ref}
      className={ProfileLink()}
      style={{ "--game-color": GameColor, "--bg": `url(${imgUrl})` }}
    >
      <div className="profile-avatar">
        <div className="avatar" />
        {GameIcon && <GameIcon />}
      </div>
      <span className="title">
        <span className="name">
          <span className="type-form--button">{name}</span>
          {tagLine && (
            <span className="tagline shade1 type-body2-form"> #{tagLine}</span>
          )}
        </span>
        {shard && <span className="shard type-caption--semi">{shard}</span>}
      </span>
    </a>
  );
};

const formatAccountsToLinks = ({
  loggedInAccounts,
  gameSymbol: currentGameSymbol,
}) => {
  let currentGameLinks = [];
  let otherGameLinks = [];

  for (const { gameSymbol } of gamesList.filter((i) => i.isActive)) {
    const gameShortName = gameShortNameReassign(GAME_SHORT_NAMES[gameSymbol]);
    const shortName = gameShortName === "tft" ? "lol" : gameShortName; // Special case game short name re-assignment (non-global)
    const gameAccounts = loggedInAccounts[shortName];
    const entries = isObject(gameAccounts)
      ? Object.entries(gameAccounts)
      : undefined;
    if (Array.isArray(entries) && entries.length) {
      const links = entries
        .filter(
          ([_, account]) =>
            !(account instanceof Error) && // Must not be an Error instance
            isObject(account) && // Must be a valid object
            !isEmpty(account), // Empty object is not permitted
        )
        .map(([accountKey, account]) => ({
          ...getNavProfile({ gameSymbol, account }),
          accountKey,
          gameSymbol,
        }))
        .filter((profile) => profile.profileUrl && profile.name);

      if (gameSymbol === currentGameSymbol) {
        currentGameLinks = links;
      } else {
        otherGameLinks = otherGameLinks.concat(links);
      }
    }
  }

  if (otherGameLinks.length) {
    return [
      {
        category: currentGameSymbol,
        links: currentGameLinks,
      },
      {
        category: ["common:otherProfiles", "Other Profiles"],
        links: otherGameLinks,
      },
    ];
  }

  return [
    {
      category: currentGameSymbol,
      links: currentGameLinks,
    },
  ];
};

const getHomeHref = (gameSymbol) => {
  return gameSymbol ? `/${GAME_SHORT_NAMES[gameSymbol]}` : "/";
};

const ProfileIconContainer = styled("div")`
  display: flex;
  width: var(--sp-6);
  height: var(--sp-6);
  cursor: pointer;
`;

const NavMenuList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);

  li {
    position: relative;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-2);

    color: var(--shade0);

    overflow: hidden;

    a,
    button {
      flex: 1;
    }

    svg {
      width: var(--sp-5_5);
      height: var(--sp-5_5);
    }
  }
`;

const GamesProfilesDropdown = () => {
  const gameSymbol = useCurrentGame();
  const { currentPath } = useRoute({
    isGlobal: true,
  });
  const state = useSnapshot(readState);
  const loggedInAccounts = state.settings.loggedInAccounts;
  const accountLinks = formatAccountsToLinks({
    loggedInAccounts,
    gameSymbol,
  });
  const currentAccount = useCurrentAccount(gameSymbol);

  const currentProfile = currentAccount && {
    ...getNavProfile({ gameSymbol, account: currentAccount }),
    gameSymbol,
  };

  const homeHref = getHomeHref(gameSymbol);

  const active =
    currentPath === (currentProfile ? currentProfile.profileUrl : homeHref);

  const [isProfilesOpen, setProfilesOpen] = useState(false);

  const openProfiles = useCallback(() => {
    setProfilesOpen(true);
  }, []);

  const closeProfiles = useCallback(() => {
    setProfilesOpen(false);
  }, []);

  const ref = useDropdownRef({
    onClickOutside: closeProfiles,
    onWindowResize: closeProfiles,
    onAssignNode: () => {},
  });

  const isTouch = useMediaQuery(touchOnly);

  const { t } = useTranslation();

  return (
    <GamesProfilesWrapper ref={ref} className="games-profile-dropdown">
      {currentProfile && GAME_ACTIVE_MAP[currentProfile.gameSymbol] === true ? (
        <NavProfile
          profile={currentProfile}
          onClick={openProfiles}
          active={active}
        />
      ) : IS_APP ? null : (
        <a
          href={homeHref}
          {...classNames(
            "nav-profile-link type-form--button",
            active && "active",
          )}
        >
          {t("common:navigation.home", "Home")}
        </a>
      )}

      {active && <div className="active-border" />}

      {isProfilesOpen && (
        <div className="profile-dropdown desktop-only">
          {accountLinks.map((account, key) => {
            const { category, links } = account;
            const categoryName: Translation = Array.isArray(category)
              ? (category as Translation)
              : GAME_NAME_MAP[category];

            return (
              <div className="list" key={key}>
                <div className="category">
                  <span className="type-overline">{t(...categoryName)}</span>
                </div>
                <div className="profiles">
                  {Object.values(links)
                    .filter(
                      (profile) => GAME_ACTIVE_MAP[profile.gameSymbol] === true,
                    )
                    .map((profile) => (
                      <ProfileMenuLink
                        key={profile.profileUrl}
                        profile={profile}
                        onClick={closeProfiles}
                      />
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isTouch && (
        <FullscreenOverlay
          className="mobile-only"
          isOpen={isProfilesOpen}
          onModalShow={openProfiles}
          onModalClose={closeProfiles}
        >
          <DialogWindow width={550}>
            <DialogContent>
              <DialogHeader
                Icon={
                  <ProfileIconContainer>
                    <GuestProfile />
                  </ProfileIconContainer>
                }
                title={["common:nav.selectProfile", "Select Profile"]}
                onPressClose={closeProfiles}
              />

              <DialogBody>
                <NavMenuList>
                  {accountLinks.map((account, key) => {
                    const { category, links } = account;
                    const categoryName: Translation = Array.isArray(category)
                      ? (category as Translation)
                      : GAME_NAME_MAP[category];

                    return (
                      <Fragment key={key}>
                        <MenuCategory text={categoryName} />

                        <ul>
                          {Object.values(links).map((profile, key) => {
                            return (
                              <li key={key}>
                                <ProfileMenuLink
                                  profile={profile}
                                  onClick={closeProfiles}
                                />
                              </li>
                            );
                          })}
                        </ul>
                      </Fragment>
                    );
                  })}
                </NavMenuList>
              </DialogBody>
            </DialogContent>
          </DialogWindow>
        </FullscreenOverlay>
      )}
    </GamesProfilesWrapper>
  );
};

export default React.memo(GamesProfilesDropdown);
